import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { emailReminderService, ReminderEmail } from '../../services/emailReminderService';

const EmailReminders: React.FC = () => {
  const [emails, setEmails] = useState<ReminderEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<ReminderEmail | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const emailsData = await emailReminderService.getReminderEmails();
      setEmails(emailsData);
    } catch (error) {
      console.error('Error fetching reminder emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = (email: ReminderEmail) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEmailDialogOpen(false);
    setSelectedEmail(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to strip HTML tags and clean up the email body
  const stripHtmlAndFormat = (htmlString: string): string => {
    if (!htmlString) return '';
    
    try {
      // Create a temporary div element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      
      // Get text content (this automatically strips HTML tags and preserves text from inside tags)
      let text = tempDiv.textContent || tempDiv.innerText || '';
      
      // Replace escaped newline characters (\n) with actual newlines
      text = text.replace(/\\n/g, '\n');
      
      // Replace <br> tags that might have been converted to text with newlines
      text = text.replace(/<br\s*\/?>/gi, '\n');
      
      // Clean up multiple consecutive newlines (more than 2) to just 2 newlines
      text = text.replace(/\n{3,}/g, '\n\n');
      
      // Clean up spaces before newlines
      text = text.replace(/ +\n/g, '\n');
      
      // Clean up newlines followed by spaces
      text = text.replace(/\n +/g, '\n');
      
      // Trim leading/trailing whitespace and newlines
      text = text.trim();
      
      return text;
    } catch (error) {
      console.error('Error parsing HTML:', error);
      // Fallback: simple regex-based HTML tag removal
      return htmlString
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\\n/g, '\n') // Replace \n with actual newlines
        .replace(/\n{3,}/g, '\n\n') // Clean up multiple newlines
        .trim();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Email Reminders
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              View all reminder emails sent from your organization
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchEmails}
              disabled={loading}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            flex: 1,
            background: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(102, 126, 234, 0.05)',
          }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Total Emails
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {emails.length}
          </Typography>
        </Paper>
      </Box>

      {/* Emails Table */}
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No reminder emails found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              emails.map((email) => (
                <TableRow
                  key={email.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(102, 126, 234, 0.05)',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => handleEmailClick(email)}
                >
                  <TableCell>{formatDate(email.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {email.from}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {email.to}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {email.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Email">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmailClick(email);
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(102, 126, 234, 0.1)'
                              : 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Email Detail Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
              : '#ffffff',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedEmail && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  From
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedEmail.from}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  To
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedEmail.to}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Subject
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedEmail.subject}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1, display: 'block' }}>
                  Body
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    maxHeight: '400px',
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {stripHtmlAndFormat(selectedEmail.body)}
                  </Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Sent At
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                  {formatDate(selectedEmail.created_at)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailReminders;

